import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  Avatar,
  Button,
  Chip,
  Divider,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure
} from '@heroui/react';
import {
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  Share2,
  MoreHorizontal,
  Flag,
  Calendar,
  MapPin,
  BarChart3,
  Eye,
  EyeOff,
  Trash2,
  Edit3
} from 'lucide-react';
import CalendarLinks from './CalendarLinks';
import CommentSection from './CommentSection';
import PostEditor from './PostEditor';
import { useStomp } from '../../contexts/StompContext';
import { useAuth } from '../../contexts/AuthContext';

const Post = ({ post, onPostUpdated, onPostDeleted }) => {
  const { user } = useAuth();
  const { addMessageHandler, removeMessageHandler, isConnected } = useStomp();

  const [upvotes, setUpvotes] = useState(post?.upvotes || 0);
  const [downvotes, setDownvotes] = useState(post.downvotes || 0);
  const [userVote, setUserVote] = useState(post.myvote?.direction || null); // 'up', 'down', or null
  const [showComments, setShowComments] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comment_total_full || post.comment_count || 0);

  // Poll state
  const [pollOptions, setPollOptions] = useState(post.poll_options || []);
  const [hasVotedInPoll, setHasVotedInPoll] = useState(false);
  const [isPollVoting, setIsPollVoting] = useState(false);

  const { isOpen: isShareOpen, onOpen: onShareOpen, onOpenChange: onShareOpenChange } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onOpenChange: onEditOpenChange } = useDisclosure();

  // Update vote state when post data changes (but don't override STOMP updates)
  useEffect(() => {
    console.log('Post: Initializing vote state from post data:', {
      postId: post._id?.['$oid'] || post._id,
      myvote: post.myvote,
      myvoteDirection: post.myvote?.direction,
      upvotes: post.upvotes,
      downvotes: post.downvotes,
      currentUserVote: userVote,
      currentUpvotes: upvotes,
      currentDownvotes: downvotes
    });

    // Only update userVote if it's not already set (initial load)
    if (userVote === null || userVote === undefined) {
      setUserVote(post.myvote?.direction || null);
    }

    // Only update vote counts if they're not already set (initial load)
    if (upvotes === 0 && downvotes === 0) {
      setUpvotes(post.upvotes || 0);
      setDownvotes(post.downvotes || 0);
    }

    setCommentCount(post.comment_total_full || post.comment_count || 0);
  }, [post.myvote, post.upvotes, post.downvotes, post.comment_total_full, post.comment_count]);

  // Debug userVote changes
  useEffect(() => {
    console.log('Post: userVote state changed to:', userVote);
  }, [userVote]);

  // STOMP real-time updates
  useEffect(() => {
    if (!isConnected) return;

    const postId = post._id?.['$oid'] || post._id;

    // Handle vote notifications
    const handleVoteUpdate = (message, topic) => {
      console.log('Post: Received vote update:', message);

      let messagePostId = null;
      let voteData = null;

      // Handle the vote_update format
      if (message.vote_update?.post) {
        messagePostId = message.vote_update.post._id?.['$oid'] || message.vote_update.post._id;
        voteData = {
          upvotes: message.vote_update.upvotes,
          downvotes: message.vote_update.downvotes,
          new_direction: message.vote_update.new_direction,
          voter: message.vote_update.voter
        };
      }

      if (messagePostId === postId && voteData) {
        console.log('Post: Vote update is for this post, updating counts');
        console.log('Post: Vote data:', voteData);

        // Always update vote counts from authoritative server data
        console.log('Post: Setting vote counts:', {
          upvotes: voteData.upvotes,
          upvotesType: typeof voteData.upvotes,
          downvotes: voteData.downvotes,
          downvotesType: typeof voteData.downvotes
        });

        // Ensure we're setting numbers, not objects
        const safeUpvotes = typeof voteData.upvotes === 'number' ? voteData.upvotes : 0;
        const safeDownvotes = typeof voteData.downvotes === 'number' ? voteData.downvotes : 0;

        setUpvotes(safeUpvotes);
        setDownvotes(safeDownvotes);

        // Update user vote state if this is the current user's vote
        if (voteData.voter && user && voteData.voter.username === user.username) {
          console.log('Post: This is current user vote, updating button state:', {
            oldUserVote: userVote,
            newDirection: voteData.new_direction,
            voter: voteData.voter.username,
            currentUser: user.username,
            comparison: voteData.voter.username === user.username
          });
          setUserVote(voteData.new_direction);
          setIsVoting(false); // Re-enable voting now that our vote is confirmed
          console.log('Post: Updated user vote state to:', voteData.new_direction);
          console.log('Post: Current userVote state after update:', userVote); // This might still show old value due to React batching
        } else {
          console.log('Post: Not current user vote:', {
            hasVoter: !!voteData.voter,
            hasUser: !!user,
            voterUsername: voteData.voter?.username,
            currentUsername: user?.username,
            matches: voteData.voter?.username === user?.username
          });
        }
      }
    };

    // Handle comment notifications
    const handleCommentUpdate = (message, topic) => {
      console.log('Post: Received comment update:', message);

      if (message.notification?.comment && message.notification?.post) {
        const messagePostId = message.notification.post._id?.['$oid'] || message.notification.post._id;

        if (messagePostId === postId) {
          console.log('Post: Comment update is for this post');

          // Don't update comment count here - Timeline component handles it
          // The updated count will come through props when Timeline updates the post data
          console.log('Post: Comment count will be updated via props from Timeline component');
        }
      }
    };

    // Handle poll update notifications
    const handlePollUpdate = (message, topic) => {
      console.log('Post: Received poll update:', message);

      if (message.type === 'poll_update' && message.post_id === postId && message.poll_options) {
        console.log('Post: Poll update is for this post, updating poll options');
        setPollOptions(message.poll_options);

        // Check if user has voted after the update
        const userResponse = message.poll_options.find(option => {
          return option.poll_option_responses?.some(response =>
            response.user_id === user?._id?.['$oid'] || response.user_id === user?._id
          );
        });

        if (userResponse && !hasVotedInPoll) {
          setHasVotedInPoll(true);
        }
      }
    };

    // Register handlers
    const removeVoteHandler = addMessageHandler('vote', handleVoteUpdate);
    const removeCommentHandler = addMessageHandler('comment', handleCommentUpdate);
    const removePollHandler = addMessageHandler('timeline', handlePollUpdate);

    console.log('Post: Registered STOMP handlers for post', postId);

    // Cleanup
    return () => {
      removeVoteHandler();
      removeCommentHandler();
      removePollHandler();
      console.log('Post: Removed STOMP handlers for post', postId);
    };
  }, [isConnected, addMessageHandler]); // Remove post._id and user as they shouldn't change for the same post instance

  // Fetch updated post data for accurate vote counts
  const fetchUpdatedPostData = async () => {
    try {
      const postId = post._id?.['$oid'] || post._id;
      const response = await fetch(`/api/v1/posts/${postId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const updatedPost = data.posts;

        if (updatedPost) {
          setUpvotes(updatedPost.upvotes || 0);
          setDownvotes(updatedPost.downvotes || 0);
          setUserVote(updatedPost.myvote?.direction || null);
          setCommentCount(updatedPost.comment_total_full || updatedPost.comment_count || 0);

          console.log('Post: Updated with fresh data from server');
        }
      }
    } catch (error) {
      console.error('Post: Error fetching updated data:', error);
    }
  };

  // Check if user has already voted in poll on component mount
  React.useEffect(() => {
    if (pollOptions && pollOptions.length > 0) {
      const userResponse = pollOptions.find(option => {
        // Handle both boolean and object responses for is_user_response
        const isUserResponse = option.is_user_response;
        return isUserResponse && (typeof isUserResponse === 'boolean' ? isUserResponse : true);
      });
      if (userResponse) {
        setHasVotedInPoll(true);
      }
    }
  }, [pollOptions]);

  // Safety check for missing or invalid post data (after all hooks)
  if (!post || !post._id || !post.user) {
    console.warn('Post component received invalid post data:', post);
    return null;
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return diffInMinutes <= 0 ? 'agora' : `${diffInMinutes}m`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays === 1) return '1 dia';
      if (diffInDays < 7) return `${diffInDays} dias`;
      return date.toLocaleDateString('pt-BR');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Geral': 'default',
      'Segurança': 'danger',
      'Eventos': 'secondary',
      'Votação': 'primary',
      'Marketplace': 'success'
    };
    return colors[category] || 'default';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Eventos':
        return <Calendar className="w-3 h-3" />;
      case 'Votação':
        return <BarChart3 className="w-3 h-3" />;
      case 'Segurança':
        return <Flag className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const handlePollVote = async (pollOptionId) => {
    if (isPollVoting || hasVotedInPoll) return;

    setIsPollVoting(true);

    try {
      const postId = post._id?.['$oid'] || post._id;
      console.log(`Post: Voting on poll option ${pollOptionId} for post ${postId}`);

      const response = await fetch(`/api/v1/posts/${postId}/poll_responses/${pollOptionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          poll_response: {
            poll_option_id: pollOptionId
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Post: Poll vote successful:', data);

        // Update poll options with new data
        if (data.post && data.post.poll_options) {
          setPollOptions(data.post.poll_options);
          setHasVotedInPoll(true);
        }
      } else {
        console.error('Post: Failed to vote in poll');
        alert('Erro ao votar na enquete');
      }
    } catch (error) {
      console.error('Post: Error voting in poll:', error);
      alert('Erro de rede ao votar na enquete');
    } finally {
      setIsPollVoting(false);
    }
  };

  const calculatePollPercentage = (optionCount, totalVotes) => {
    if (totalVotes === 0) return 0;
    return Math.round((optionCount / totalVotes) * 100);
  };

  const handleVote = async (voteType) => {
    if (isVoting) return;

    setIsVoting(true);

    try {
      const endpoint = voteType === 'up' ? 'upvote' : 'downvote';
      const postId = post._id?.['$oid'] || post._id;

      console.log(`Post: ${voteType}voting post ${postId}`);

      const response = await fetch(`/api/v1/posts/${postId}/${endpoint}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        console.log(`Post: Vote API call successful, waiting for STOMP update`);
        // No optimistic updates - wait for STOMP message to update UI
      } else {
        console.error(`Post: Failed to ${voteType}vote`);
        setIsVoting(false); // Re-enable voting on error
      }
    } catch (error) {
      console.error(`Post: Error ${voteType}voting:`, error);
      setIsVoting(false); // Re-enable voting on error
    }
    // Note: setIsVoting(false) will be called when STOMP message arrives
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta postagem?')) {
      return;
    }

    try {
      const postId = post._id?.['$oid'] || post._id;
      const response = await fetch(`/api/v1/posts/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        // Hide the post immediately
        setIsHidden(true);
        // Notify parent component to remove from timeline
        if (onPostDeleted) {
          onPostDeleted(postId);
        }
      } else {
        alert('Erro ao excluir postagem');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Erro ao excluir postagem');
    }
  };

  const handleEdit = () => {
    onEditOpen();
  };

  const handlePostUpdated = (updatedPost) => {
    if (onPostUpdated) {
      onPostUpdated(updatedPost);
    }
    onEditOpenChange(false);
  };

  const handleFlag = async () => {
    try {
      const postId = post._id?.['$oid'] || post._id;
      const response = await fetch(`/api/v1/posts/${postId}/flag`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        alert('Postagem reportada com sucesso');
      } else {
        alert('Erro ao reportar postagem');
      }
    } catch (error) {
      console.error('Post: Error flagging post:', error);
      alert('Erro de rede ao reportar postagem');
    }
  };

  const handleShare = () => {
    onShareOpen();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copiado para a área de transferência!');
    });
  };

  // Check if post should be hidden due to downvotes
  const shouldHidePost = (post.downvotes || 0) >= 5 || post.flagged_by;

  if (shouldHidePost && !isHidden) {
    return (
      <Card className="border border-gray-200 shadow-post bg-white rounded-nextui">
        <CardBody className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-gray-500">
              <EyeOff className="w-4 h-4" />
              <span className="text-sm">
                Esta postagem foi ocultada devido a relatórios da comunidade
              </span>
            </div>
            <Button
              size="sm"
              variant="light"
              onPress={() => setIsHidden(true)}
              startContent={<Eye className="w-4 h-4" />}
            >
              Mostrar mesmo assim
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card
        className="w-full border border-gray-200 shadow-post hover:shadow-post-hover transition-all duration-200 bg-white rounded-nextui"
        data-post-id={post._id?.['$oid'] || post._id}
      >
        <CardBody className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-start space-x-3 flex-1">
              <Avatar
                src={post.user?.avatar}
                name={post.user?.username}
                size="sm"
                className="flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 flex-wrap">
                  <span className="font-semibold text-sm text-gray-900">
                    {post.user?.username || 'Usuário'}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {formatDate(post.created_at)}
                  </span>
                  {post.post_category && (
                    <Chip
                      size="sm"
                      color={getCategoryColor(post.post_category.name)}
                      variant="flat"
                      startContent={getCategoryIcon(post.post_category.name)}
                    >
                      {post.post_category.name}
                    </Chip>
                  )}
                </div>
              </div>
            </div>

            <Dropdown
              classNames={{
                base: "before:bg-default-200",
                content: "bg-white shadow-2xl border border-gray-200 rounded-lg p-0"
              }}
            >
              <DropdownTrigger>
                <Button isIconOnly size="sm" variant="light" className="text-gray-400">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                classNames={{
                  base: "bg-white",
                  list: "bg-white"
                }}
              >
                <DropdownItem
                  key="flag"
                  startContent={<Flag className="w-4 h-4" />}
                  onPress={handleFlag}
                  className="text-danger"
                >
                  Reportar postagem
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* Content */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg text-gray-900 leading-tight">
              {post.title}
            </h3>

            {post.text && (
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {post.text}
              </p>
            )}

            {/* Image */}
            {post.image_url && post.image_url !== '/images/original/missing.png' && (
              <div className="rounded-lg overflow-hidden border">
                <img
                  src={post.image_url.startsWith('//') ? `https:${post.image_url}` : post.image_url}
                  alt=""
                  className="w-full h-auto max-h-96 object-cover"
                />
              </div>
            )}

            {/* Location */}
            {post.location && (
              <div className="flex items-center space-x-1 text-gray-500 text-sm">
                <MapPin className="w-4 h-4" />
                <span>Localização compartilhada</span>
              </div>
            )}

            {/* Event Date */}
            {post.event_date && (
              <div className="flex items-center space-x-1 text-gray-500 text-sm">
                <Calendar className="w-4 h-4" />
                <span>{new Date(post.event_date).toLocaleDateString('pt-BR')}</span>
              </div>
            )}

            {/* Calendar Links for Events */}
            {(post.type === 'event' || post.post_category?.name === 'Eventos') && post.calendar_links && (
              <CalendarLinks calendarLinks={post.calendar_links} />
            )}

            {/* Poll Options */}
            {pollOptions && pollOptions.length > 0 && (
              <Card className="bg-blue-50 border-blue-200 rounded-nextui">
                <CardBody className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <BarChart3 className="w-4 h-4 text-blue-600" />
                      <h4 className="font-semibold text-blue-800">
                        {hasVotedInPoll ? 'Resultados da Votação' : 'Escolha uma opção'}
                      </h4>
                    </div>

                    <div className="space-y-2">
                      {hasVotedInPoll ? (
                        // Show results
                        (() => {
                          const totalVotes = pollOptions.reduce((sum, option) => sum + (option.count || 0), 0);
                          const sortedOptions = [...pollOptions].sort((a, b) => (b.count || 0) - (a.count || 0));

                          return sortedOptions.map((option, index) => {
                            const percentage = calculatePollPercentage(option.count || 0, totalVotes);
                            const isUserChoice = option.is_user_response && (typeof option.is_user_response === 'boolean' ? option.is_user_response : true);

                            return (
                              <div key={option._id?.['$oid'] || option._id || index} className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm font-medium ${isUserChoice ? 'text-blue-700' : 'text-gray-700'}`}>
                                    {isUserChoice && '✓ '}{option.label}
                                  </span>
                                  <span className="text-sm text-gray-600">
                                    {percentage}% ({option.count || 0})
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                      isUserChoice ? 'bg-blue-600' : 'bg-blue-400'
                                    }`}
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            );
                          });
                        })()
                      ) : (
                        // Show voting options
                        <div className="space-y-2">
                          {pollOptions.map((option, index) => (
                            <label
                              key={option._id?.['$oid'] || option._id || index}
                              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-blue-100 cursor-pointer transition-colors"
                            >
                              <input
                                type="radio"
                                name={`poll-${post._id?.['$oid'] || post._id}`}
                                value={option._id?.['$oid'] || option._id}
                                onChange={() => handlePollVote(option._id?.['$oid'] || option._id)}
                                disabled={isPollVoting}
                                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700 flex-1">
                                {option.label}
                              </span>
                            </label>
                          ))}

                          {isPollVoting && (
                            <div className="text-center py-2">
                              <div className="inline-flex items-center space-x-2 text-blue-600">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                <span className="text-sm">Registrando seu voto...</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {hasVotedInPoll && (
                      <div className="text-xs text-blue-600 bg-blue-100 p-2 rounded">
                        ✅ Obrigado por participar! Você já votou nesta enquete.
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </CardBody>

        <Divider />

        <CardFooter className="p-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-1">
              {/* Upvote */}
              <Button
                size="sm"
                variant={userVote === 'up' ? 'solid' : 'light'}
                color={userVote === 'up' ? 'success' : 'default'}
                onPress={() => handleVote('up')}
                isLoading={isVoting}
                className={`min-w-16 ${userVote === 'up' ? 'shadow-md !bg-green-500 !text-white' : ''}`}
              >
                {!isVoting && <ThumbsUp className={`w-4 h-4 mr-1 ${userVote === 'up' ? 'text-white' : ''}`} />}
                {upvotes}
              </Button>

              {/* Downvote */}
              <Button
                size="sm"
                variant={userVote === 'down' ? 'solid' : 'light'}
                color={userVote === 'down' ? 'danger' : 'default'}
                onPress={() => handleVote('down')}
                isLoading={isVoting}
                className={`min-w-16 ${userVote === 'down' ? 'shadow-md !bg-red-500 !text-white' : ''}`}
              >
                {!isVoting && <ThumbsDown className={`w-4 h-4 mr-1 ${userVote === 'down' ? 'text-white' : ''}`} />}
                {downvotes}
              </Button>

              {/* Comments */}
              <Button
                size="sm"
                variant="light"
                startContent={<MessageCircle className="w-4 h-4" />}
                onPress={() => setShowComments(!showComments)}
                className="min-w-16"
              >
                {commentCount}
              </Button>
            </div>

            <div className="flex items-center space-x-2">
              {/* Edit and Delete buttons - only show for post owner */}
              {user && post.user && (user.id === post.user.id || user._id === post.user._id) && (
                <>
                  <Button
                    size="sm"
                    variant="light"
                    isIconOnly
                    onPress={handleEdit}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <Edit3 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    isIconOnly
                    onPress={handleDelete}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}

              {/* Share */}
              <Button
                size="sm"
                variant="light"
                isIconOnly
                onPress={handleShare}
                className="text-gray-500"
              >
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>

      {/* Comment Section */}
      <CommentSection
        post={post}
        initialComments={post.comments || []}
        isExpanded={showComments}
        onToggle={() => setShowComments(!showComments)}
        onPostUpdate={onPostUpdated}
      />

      {/* Share Modal */}
      <Modal isOpen={isShareOpen} onOpenChange={onShareOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                <div className="flex items-center space-x-2">
                  <Share2 className="w-5 h-5" />
                  <span>Compartilhar Postagem</span>
                </div>
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Link da postagem:</p>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 p-2 bg-gray-100 rounded text-sm">
                        {window.location.origin}/posts/{post._id?.['$oid'] || post._id}
                      </code>
                      <Button
                        size="sm"
                        color="primary"
                        onPress={() => copyToClipboard(`${window.location.origin}/posts/${post._id?.['$oid'] || post._id}`)}
                      >
                        Copiar
                      </Button>
                    </div>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="primary" onPress={onClose}>
                  Fechar
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditOpen}
        onOpenChange={onEditOpenChange}
        size="2xl"
        backdrop="opaque"
        classNames={{
          backdrop: "bg-black/50",
          base: "border-none",
          wrapper: "p-4",
          body: "py-6",
          header: "border-b-[1px] border-gray-200",
          closeButton: "hover:bg-gray-100"
        }}
      >
        <ModalContent className="bg-white shadow-2xl rounded-nextui-lg">
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 bg-white">
                <div className="flex items-center space-x-2">
                  <Edit3 className="w-5 h-5 text-primary" />
                  <span className="text-lg font-semibold text-gray-900">Editar Postagem</span>
                </div>
              </ModalHeader>
              <ModalBody className="bg-white">
                <PostEditor
                  editMode={true}
                  initialPost={post}
                  onPostCreated={handlePostUpdated}
                />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default Post;
